import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  addHours,
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  LayoutGrid,
  List,
  Loader2,
  RefreshCw,
  UserPlus,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  cancelAgentCalendarAppointment,
  createAgentCalendarAppointment,
  getAgentCalendar,
  getAgentCalendarAppointments,
  getCalendarIntegrations,
  getApiError,
  triggerCalendarIntegrationSync,
  triggerMyCalendarIntegrationSync,
} from '../lib/api';
import { cn, formatDateTime } from '../lib/utils';

function aptStart(apt) {
  return new Date(apt.start_at || apt.starts_at);
}

function getRange(view, anchor) {
  if (view === 'day') {
    return { from: startOfDay(anchor), to: endOfDay(anchor) };
  }
  if (view === 'week') {
    const ws = startOfWeek(anchor, { weekStartsOn: 0 });
    const we = endOfWeek(anchor, { weekStartsOn: 0 });
    return { from: startOfDay(ws), to: endOfDay(we) };
  }
  return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
}

function shiftAnchor(view, anchor, dir) {
  if (view === 'day') return addDays(anchor, dir);
  if (view === 'week') return addDays(anchor, 7 * dir);
  return addMonths(anchor, dir);
}

function rangeLabel(view, anchor) {
  const { from, to } = getRange(view, anchor);
  if (view === 'day') return format(from, 'EEEE, MMMM d, yyyy');
  if (view === 'week') {
    if (isSameMonth(from, to)) {
      return `${format(from, 'MMM d')} – ${format(to, 'd, yyyy')}`;
    }
    return `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
  }
  return format(from, 'MMMM yyyy');
}

function normalizeAgentIdFragment(raw) {
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  return s || null;
}

function headerAgentId(headers) {
  if (!headers || typeof headers !== 'object') return null;
  const v =
    headers['x-agent-id'] ??
    headers['X-Agent-Id'] ??
    (typeof headers.get === 'function' ? headers.get('x-agent-id') || headers.get('X-Agent-Id') : null);
  return normalizeAgentIdFragment(v);
}

/**
 * Priority: response body `agent_id` (200 or 404 JSON) → route/query agent id → `X-Agent-Id` on the response.
 */
function pickBookingAgentId({ body, headers, routeAgentId }) {
  if (body && typeof body === 'object') {
    const top = normalizeAgentIdFragment(body.agent_id ?? body.agentId);
    if (top) return top;
    const cal = body.calendar;
    if (cal && typeof cal === 'object') {
      const nested = normalizeAgentIdFragment(
        cal.agent_id ?? cal.agentId ?? cal.agent?.agent_id ?? cal.agent?.agentId
      );
      if (nested) return nested;
    }
  }
  const route = normalizeAgentIdFragment(routeAgentId);
  if (route) return route;
  return headerAgentId(headers);
}

function integrationProviderKey(row) {
  return String(row.provider || row.type || '').toLowerCase();
}

/** Treat as active unless status explicitly says otherwise. */
function hasActiveGoogleOrMicrosoftIntegration(integrations) {
  return integrations.some((row) => {
    const p = integrationProviderKey(row);
    if (p !== 'google' && p !== 'microsoft') return false;
    const st = String(row.status ?? 'active').toLowerCase();
    if (st === 'revoked' || st === 'error') return false;
    return true;
  });
}

function parseAppointmentList(res) {
  const list =
    res.data?.appointments ||
    res.data?.items ||
    res.data?.results ||
    (Array.isArray(res.data) ? res.data : []);
  return Array.isArray(list) ? list : [];
}

function getAppointmentSourceSessionId(apt) {
  const v = apt.source_session_id ?? apt.sourceSessionId ?? apt.session_id ?? apt.sessionId;
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s || null;
}

function getAppointmentSourceChannelRaw(apt) {
  const v = apt.source_channel ?? apt.sourceChannel ?? apt.channel;
  if (v === undefined || v === null) return '';
  return String(v).trim();
}

function formatChannelLabel(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return '';
  const map = {
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    messenger: 'Messenger',
    voice: 'Voice',
    phone: 'Phone',
    call: 'Call',
    pstn: 'PSTN',
    facebook: 'Facebook',
    instagram: 'Instagram',
    public: 'Public link',
    manual: 'Manual',
  };
  if (map[s]) return map[s];
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Prefer API `source_channel`; fall back to `source` for list display. */
function describeAppointmentSource(apt) {
  const ch = getAppointmentSourceChannelRaw(apt);
  if (ch) return formatChannelLabel(ch);
  const src = apt.source;
  if (src !== undefined && src !== null && String(src).trim() !== '') {
    return formatChannelLabel(String(src).trim());
  }
  return 'Public link';
}

const CALL_DETAIL_CHANNELS = new Set([
  'voice',
  'phone',
  'call',
  'pstn',
  'telephony',
  'vapi',
  'inbound',
  'outbound',
]);

const MESSAGE_DETAIL_CHANNELS = new Set([
  'sms',
  'whatsapp',
  'messenger',
  'facebook',
  'instagram',
  'chat',
  'message',
  'text',
  'webchat',
  'telegram',
  'imessage',
]);

function routingChannelForAppointment(apt) {
  const explicit = getAppointmentSourceChannelRaw(apt).toLowerCase();
  if (explicit) return explicit;
  const src = String(apt.source || '')
    .trim()
    .toLowerCase();
  if (CALL_DETAIL_CHANNELS.has(src) || src === 'voice') return src;
  if (MESSAGE_DETAIL_CHANNELS.has(src)) return src;
  return '';
}

/**
 * Map `source_session_id` + channel to /calls/:id or /messages/:id (same id the detail APIs expect).
 */
function getAppointmentConversationLink(apt) {
  const sessionId = getAppointmentSourceSessionId(apt);
  const c = routingChannelForAppointment(apt);
  if (!sessionId || !c) return null;
  if (c === 'public' || c === 'manual' || c === 'web') return null;

  if (CALL_DETAIL_CHANNELS.has(c)) {
    return { to: `/calls/${encodeURIComponent(sessionId)}`, label: 'Call details' };
  }
  if (MESSAGE_DETAIL_CHANNELS.has(c)) {
    return { to: `/messages/${encodeURIComponent(sessionId)}`, label: 'Message details' };
  }
  return null;
}

function toDateTimeLocalValue(date) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

function AppointmentCard({ apt, onCancel }) {
  const id = apt.appointment_id || apt.id;
  const cust = apt.customer || {};
  const name = cust.name || apt.customer_name || 'Customer';
  const phone = cust.phone || apt.customer_phone || '—';
  const start = apt.start_at || apt.starts_at;
  const end = apt.end_at || apt.ends_at;
  const sessionId = getAppointmentSourceSessionId(apt);
  const convo = getAppointmentConversationLink(apt);

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="font-medium text-slate-900 truncate">{name}</p>
          <p className="text-xs text-slate-500">{phone}</p>
          <p className="text-sm text-slate-700 mt-1">
            {formatDateTime(start)} – {formatDateTime(end)}
          </p>
          {(cust.address || apt.customer_address) && (
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{cust.address || apt.customer_address}</p>
          )}
          {sessionId && (
            <p className="text-xs text-slate-500 mt-1.5 break-all">
              <span className="text-slate-400 mr-1">Session</span>
              <span className="font-mono text-[11px] text-slate-600" title={sessionId}>
                {sessionId}
              </span>
            </p>
          )}
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Badge variant="secondary">{describeAppointmentSource(apt)}</Badge>
            {convo && (
              <Button variant="outline" size="sm" className="h-8 text-teal-700 border-teal-200" asChild>
                <Link to={convo.to}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1" />
                  {convo.label}
                </Link>
              </Button>
            )}
            <Button variant="ghost" size="sm" className="text-red-600 h-8" onClick={() => onCancel(id)}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </li>
  );
}

export default function MyAppointmentsPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const agentIdFromQuery = searchParams.get('agentId') || searchParams.get('agent_id');
  const routeAgentId = useMemo(
    () => normalizeAgentIdFragment(params.agentId ?? agentIdFromQuery),
    [params.agentId, agentIdFromQuery]
  );

  const [calendarLoading, setCalendarLoading] = useState(true);
  const [missingCalendar, setMissingCalendar] = useState(false);
  const [bookingAgentId, setBookingAgentId] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  /** 'idle' = did not successfully load list (missing agent, error, or not fetched); 'success' = GET ok */
  const [integrationsLoadStatus, setIntegrationsLoadStatus] = useState('idle');
  const [syncingExternal, setSyncingExternal] = useState(false);

  const [view, setView] = useState('week');
  const [displayMode, setDisplayMode] = useState('calendar');
  const [anchorDate, setAnchorDate] = useState(() => new Date());

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualForm, setManualForm] = useState({
    startLocal: '',
    endLocal: '',
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
  });

  const loadCalendarMeta = useCallback(async () => {
    try {
      setCalendarLoading(true);
      const res = await getAgentCalendar({ suppressGlobalErrorToast: true });
      setMissingCalendar(false);
      setBookingAgentId(
        pickBookingAgentId({ body: res.data, headers: res.headers, routeAgentId })
      );
    } catch (error) {
      const status = error.status;
      const body = error.data;
      const headers = error.originalError?.response?.headers;
      const parsed = pickBookingAgentId({ body, headers, routeAgentId });
      setBookingAgentId(parsed);
      if (status === 404) {
        setMissingCalendar(true);
      } else {
        toast.error(error.detail || 'Failed to load calendar');
        setMissingCalendar(true);
      }
    } finally {
      setCalendarLoading(false);
    }
  }, [routeAgentId]);

  const loadIntegrations = useCallback(async () => {
    if (!bookingAgentId) {
      setIntegrations([]);
      setIntegrationsLoadStatus('idle');
      return;
    }
    try {
      const res = await getCalendarIntegrations(bookingAgentId, { suppressGlobalErrorToast: true });
      const list = res.data?.integrations ?? res.data;
      setIntegrations(Array.isArray(list) ? list : []);
      setIntegrationsLoadStatus('success');
    } catch {
      setIntegrations([]);
      setIntegrationsLoadStatus('error');
    }
  }, [bookingAgentId]);

  useEffect(() => {
    loadCalendarMeta();
  }, [loadCalendarMeta]);

  useEffect(() => {
    if (!missingCalendar && bookingAgentId) {
      loadIntegrations();
    } else {
      setIntegrations([]);
      setIntegrationsLoadStatus('idle');
    }
  }, [missingCalendar, bookingAgentId, loadIntegrations]);

  const { from, to } = useMemo(() => getRange(view, anchorDate), [view, anchorDate]);

  const loadAppointments = useCallback(async () => {
    if (missingCalendar) {
      setAppointments([]);
      return;
    }
    try {
      setLoading(true);
      const res = await getAgentCalendarAppointments(
        { from: from.toISOString(), to: to.toISOString() },
        { suppressGlobalErrorToast: true }
      );
      setAppointments(parseAppointmentList(res));
    } catch (error) {
      toast.error(error.detail || 'Failed to load appointments');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, missingCalendar]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const sorted = useMemo(
    () =>
      [...appointments].sort(
        (a, b) => aptStart(a).getTime() - aptStart(b).getTime()
      ),
    [appointments]
  );

  const countsByDay = useMemo(() => {
    const m = new Map();
    for (const a of appointments) {
      const k = format(aptStart(a), 'yyyy-MM-dd');
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [appointments]);

  const weekDays = useMemo(() => {
    const ws = startOfWeek(anchorDate, { weekStartsOn: 0 });
    const we = endOfWeek(anchorDate, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: ws, end: we });
  }, [anchorDate]);

  const handleCancel = async (appointmentId) => {
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      await cancelAgentCalendarAppointment(appointmentId);
      toast.success('Appointment cancelled');
      loadAppointments();
    } catch (error) {
      toast.error(error.detail || 'Could not cancel');
    }
  };

  const handleManualCreate = async () => {
    if (!manualForm.startLocal || !manualForm.endLocal) {
      toast.error('Start and end time are required');
      return;
    }
    if (!manualForm.name.trim() || !manualForm.phone.trim()) {
      toast.error('Customer name and phone are required');
      return;
    }
    const startAt = new Date(manualForm.startLocal).toISOString();
    const endAt = new Date(manualForm.endLocal).toISOString();
    try {
      setManualSubmitting(true);
      await createAgentCalendarAppointment({
        start_at: startAt,
        end_at: endAt,
        source: 'manual',
        customer: {
          name: manualForm.name.trim(),
          phone: manualForm.phone.trim(),
          email: manualForm.email.trim() || undefined,
          address: manualForm.address.trim() || undefined,
        },
        notes: manualForm.notes.trim() || undefined,
      });
      toast.success('Appointment created');
      setManualOpen(false);
      setManualForm({
        startLocal: '',
        endLocal: '',
        name: '',
        phone: '',
        email: '',
        address: '',
        notes: '',
      });
      loadAppointments();
    } catch (error) {
      toast.error(error.detail || 'Could not create appointment');
    } finally {
      setManualSubmitting(false);
    }
  };

  const goPrev = () => setAnchorDate((d) => shiftAnchor(view, d, -1));
  const goNext = () => setAnchorDate((d) => shiftAnchor(view, d, 1));
  const goToday = () => setAnchorDate(new Date());

  const syncDisabledNoConnection =
    integrationsLoadStatus === 'success' && !hasActiveGoogleOrMicrosoftIntegration(integrations);

  const handleExternalSyncNow = async () => {
    if (syncDisabledNoConnection) {
      toast.info('Connect Google Calendar or Outlook under Booking calendar settings first.');
      return;
    }
    try {
      setSyncingExternal(true);
      const res = bookingAgentId
        ? await triggerCalendarIntegrationSync(bookingAgentId)
        : await triggerMyCalendarIntegrationSync();
      const msg =
        (typeof res.data?.message === 'string' && res.data.message) ||
        (res.data?.synced === true && 'Calendar sync completed') ||
        (res.data?.started === true && 'Sync started — refreshing…') ||
        'Calendar sync completed';
      toast.success(msg);
      await loadAppointments();
      if (bookingAgentId) await loadIntegrations();
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || 'Sync request failed');
    } finally {
      setSyncingExternal(false);
    }
  };

  const monthForPicker = startOfMonth(anchorDate);

  if (calendarLoading) {
    return (
      <div className="space-y-4 max-w-6xl mx-auto">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (missingCalendar) {
    return (
      <div className="max-w-lg mx-auto space-y-4" data-testid="my-appointments-page">
        <h1 className="text-2xl font-jakarta font-bold text-slate-900 flex items-center gap-2">
          <CalendarIcon className="w-8 h-8 text-teal-600" />
          My appointments
        </h1>
        <Card>
          <CardHeader>
            <CardTitle>No scheduling calendar yet</CardTitle>
            <CardDescription>
              Create a calendar under an agent&apos;s booking settings to see and manage appointments here.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="bg-teal-600 hover:bg-teal-700">
              <Link to="/agents">Go to My Agents</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 pb-8" data-testid="my-appointments-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900 flex items-center gap-2">
            <CalendarIcon className="w-7 h-7 sm:w-8 sm:h-8 text-teal-600" />
            My appointments
          </h1>
          <p className="text-slate-500 text-sm mt-1">Bookings for your session&apos;s calendar</p>
        </div>
        <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto gap-2"
            onClick={handleExternalSyncNow}
            disabled={syncingExternal || syncDisabledNoConnection}
            title={
              syncDisabledNoConnection
                ? 'Connect Google Calendar or Outlook in Booking calendar settings first'
                : 'Run sync with your linked Google or Outlook calendar'
            }
            data-testid="calendar-sync-external-btn"
          >
            {syncingExternal ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Sync Google / Outlook
          </Button>
          <Button
            type="button"
            className="bg-teal-600 hover:bg-teal-700 w-full sm:w-auto"
            onClick={() => setManualOpen(true)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add manually
          </Button>
        </div>
      </div>

      <Tabs value={displayMode} onValueChange={setDisplayMode} className="w-full">
        <Card className="overflow-hidden">
          <CardHeader className="space-y-4 pb-2 sm:pb-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <ToggleGroup
                type="single"
                value={view}
                onValueChange={(v) => v && setView(v)}
                className="justify-stretch sm:justify-start grid grid-cols-3 w-full sm:w-auto sm:inline-flex"
              >
                <ToggleGroupItem value="day" aria-label="Day" className="flex-1 sm:flex-initial px-2 text-xs sm:text-sm">
                  Day
                </ToggleGroupItem>
                <ToggleGroupItem value="week" aria-label="Week" className="flex-1 sm:flex-initial px-2 text-xs sm:text-sm">
                  Week
                </ToggleGroupItem>
                <ToggleGroupItem value="month" aria-label="Month" className="flex-1 sm:flex-initial px-2 text-xs sm:text-sm">
                  Month
                </ToggleGroupItem>
              </ToggleGroup>

              <TabsList className="grid w-full grid-cols-2 lg:w-[240px] lg:ml-auto">
                <TabsTrigger value="calendar" className="gap-2">
                  <LayoutGrid className="w-4 h-4" />
                  Calendar
                </TabsTrigger>
                <TabsTrigger value="list" className="gap-2">
                  <List className="w-4 h-4" />
                  List
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="icon" className="shrink-0 h-9 w-9" onClick={goPrev} aria-label="Previous">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <p className="text-sm sm:text-base font-medium text-slate-900 text-center flex-1 min-w-[12rem] leading-snug">
                {rangeLabel(view, anchorDate)}
              </p>
              <Button type="button" variant="outline" size="icon" className="shrink-0 h-9 w-9" onClick={goNext} aria-label="Next">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button type="button" variant="secondary" size="sm" className="w-full sm:w-auto" onClick={goToday}>
                Today
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <TabsContent value="calendar" className="mt-0 outline-none">
              {loading ? (
                <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading…
                </div>
              ) : view === 'month' ? (
                <div className="flex flex-col items-center gap-4">
                  <Calendar
                    mode="single"
                    month={monthForPicker}
                    onMonthChange={(m) => setAnchorDate(startOfMonth(m))}
                    selected={anchorDate}
                    onSelect={(d) => d && setAnchorDate(d)}
                    className="rounded-md border w-full max-w-md mx-auto p-2 sm:p-3"
                    modifiers={{
                      hasBooking: (date) => (countsByDay.get(format(date, 'yyyy-MM-dd')) || 0) > 0,
                    }}
                    modifiersClassNames={{
                      hasBooking: 'bg-teal-100/90 text-teal-900 font-medium data-[selected=true]:bg-teal-600 data-[selected=true]:text-white',
                    }}
                  />
                  <p className="text-xs text-slate-500 text-center px-2">
                    Days with a teal tint have at least one appointment. Tap a day to select it.
                  </p>
                  <div className="w-full border-t border-slate-200 pt-4">
                    <p className="text-sm font-medium text-slate-800 mb-2">
                      {format(anchorDate, 'EEEE, MMMM d')}
                    </p>
                    <DayAgendaList
                      appointments={sorted.filter((a) => isSameDay(aptStart(a), anchorDate))}
                      onCancel={handleCancel}
                      emptyHint="No appointments on this day in the loaded month range."
                    />
                  </div>
                </div>
              ) : view === 'week' ? (
                <ScrollArea className="w-full -mx-1 px-1">
                  <div className="flex flex-col sm:grid sm:grid-cols-7 gap-3 min-w-0 sm:min-h-[280px]">
                    {weekDays.map((day) => {
                      const dayApts = sorted.filter((a) => isSameDay(aptStart(a), day));
                      return (
                        <div
                          key={day.toISOString()}
                          className={cn(
                            'rounded-lg border border-slate-200 bg-slate-50/50 p-2 min-h-[120px] sm:min-h-[240px]',
                            isSameDay(day, new Date()) && 'ring-2 ring-teal-400/60'
                          )}
                        >
                          <p className="text-xs font-semibold text-slate-600 mb-2">
                            <span className="block">{format(day, 'EEE')}</span>
                            <span className="text-base text-slate-900">{format(day, 'd')}</span>
                          </p>
                          <ul className="space-y-2">
                            {dayApts.length === 0 ? (
                              <li className="text-[11px] text-slate-400">—</li>
                            ) : (
                              dayApts.map((apt) => {
                                const id = apt.appointment_id || apt.id;
                                const cust = apt.customer || {};
                                const name = cust.name || apt.customer_name || 'Customer';
                                const convo = getAppointmentConversationLink(apt);
                                return (
                                  <li key={id}>
                                    <button
                                      type="button"
                                      className="w-full text-left rounded-md bg-white border border-slate-200 px-2 py-1.5 text-[11px] sm:text-xs shadow-sm hover:border-teal-300 transition-colors"
                                      onClick={() => {
                                        setAnchorDate(day);
                                        setView('day');
                                        setDisplayMode('list');
                                      }}
                                    >
                                      <span className="font-medium text-slate-800 block truncate">{name}</span>
                                      <span className="text-slate-500">{format(aptStart(apt), 'p')}</span>
                                      {convo && (
                                        <Link
                                          to={convo.to}
                                          className="mt-1 inline-flex items-center gap-0.5 text-teal-600 font-medium hover:underline"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <ExternalLink className="w-3 h-3 shrink-0" />
                                          {convo.label}
                                        </Link>
                                      )}
                                    </button>
                                  </li>
                                );
                              })
                            )}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:p-4">
                    <p className="text-lg font-semibold text-slate-900">{format(anchorDate, 'EEEE, MMMM d, yyyy')}</p>
                  </div>
                  <DayAgendaList appointments={sorted} onCancel={handleCancel} emptyHint="No appointments this day." />
                </div>
              )}
            </TabsContent>

            <TabsContent value="list" className="mt-0 outline-none">
              {loading ? (
                <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading…
                </div>
              ) : (
                <ScrollArea className="h-[min(70vh,560px)] sm:h-[min(65vh,600px)] pr-3">
                  <ul className="space-y-3">
                    {sorted.length === 0 ? (
                      <li className="text-sm text-slate-500 py-8 text-center">No appointments in this range.</li>
                    ) : (
                      sorted.map((apt) => (
                        <AppointmentCard key={apt.appointment_id || apt.id} apt={apt} onCancel={handleCancel} />
                      ))
                    )}
                  </ul>
                </ScrollArea>
              )}
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add appointment manually</DialogTitle>
            <DialogDescription>Times use your browser&apos;s local timezone.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="ma-start">Start</Label>
                <Input
                  id="ma-start"
                  type="datetime-local"
                  step={900}
                  value={manualForm.startLocal}
                  onChange={(e) =>
                    setManualForm((f) => {
                      const startLocal = e.target.value;
                      if (!startLocal) {
                        return { ...f, startLocal: '', endLocal: '' };
                      }
                      const startDate = new Date(startLocal);
                      if (Number.isNaN(startDate.getTime())) {
                        return { ...f, startLocal };
                      }
                      return {
                        ...f,
                        startLocal,
                        endLocal: toDateTimeLocalValue(addHours(startDate, 1)),
                      };
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ma-end">End</Label>
                <Input
                  id="ma-end"
                  type="datetime-local"
                  step={900}
                  value={manualForm.endLocal}
                  onChange={(e) => setManualForm((f) => ({ ...f, endLocal: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ma-name">Customer name</Label>
              <Input
                id="ma-name"
                value={manualForm.name}
                onChange={(e) => setManualForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ma-phone">Phone</Label>
              <Input
                id="ma-phone"
                value={manualForm.phone}
                onChange={(e) => setManualForm((f) => ({ ...f, phone: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ma-email">Email (optional)</Label>
              <Input
                id="ma-email"
                type="email"
                value={manualForm.email}
                onChange={(e) => setManualForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ma-address">Address (optional)</Label>
              <Textarea
                id="ma-address"
                rows={2}
                value={manualForm.address}
                onChange={(e) => setManualForm((f) => ({ ...f, address: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ma-notes">Notes (optional)</Label>
              <Textarea
                id="ma-notes"
                rows={2}
                value={manualForm.notes}
                onChange={(e) => setManualForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setManualOpen(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleManualCreate} disabled={manualSubmitting} className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700">
              {manualSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DayAgendaList({ appointments, onCancel, emptyHint }) {
  if (appointments.length === 0) {
    return <p className="text-sm text-slate-500 py-4">{emptyHint}</p>;
  }
  return (
    <ul className="space-y-3">
      {appointments.map((apt) => (
        <AppointmentCard key={apt.appointment_id || apt.id} apt={apt} onCancel={onCancel} />
      ))}
    </ul>
  );
}
